/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    localToday as sarsaLocalToday,
    pluralize,
    useFormatDateRange,
    useFormatDateTime,
} from 'sarsaparilla';
import {
    type CalendarDate,
    getLocalTimeZone,
    parseDate,
    today,
} from '@internationalized/date';

import type { DatePickerCloseAction, DatePickerState } from './DatePicker.types';
import type { FlexDateFilters } from './FlexDatePicker.types';

/**
 * Most nights a stay may span, whether typed in or picked on the calendar.
 * The one-year limit can lower it further for the type-in field.
 */
export const MAX_SELECTABLE_NIGHTS = 31;

/**
 * Latest date a stay may end, inclusive.
 * One year from today: Oct 13, 2026 allows an end date through Oct 13, 2027.
 */
export const maxSelectableDate = sarsaLocalToday.add({ years: 1 });

/**
 * Highest night count the type-in field may accept for this start date.
 * A night count of 0 keeps the end date on the start date.
 */
export function getMaxTypedNights(startDate: CalendarDate | null): number {
    if (!startDate) {
        return MAX_SELECTABLE_NIGHTS;
    }

    const nightsUntilLimit = maxSelectableDate.compare(startDate);
    return Math.max(0, Math.min(MAX_SELECTABLE_NIGHTS, nightsUntilLimit));
}

/**
 * Pulls an end date back to the one-year limit.
 * Leaves an in-range end date unchanged, including stays longer than the type-in cap.
 * The result is never earlier than the start date.
 */
export function clampEndToMaxDate(
    startDate: CalendarDate,
    endDate: CalendarDate
): CalendarDate {
    if (endDate.compare(maxSelectableDate) <= 0) {
        return endDate;
    }

    if (startDate.compare(maxSelectableDate) <= 0) {
        return maxSelectableDate;
    }

    return startDate;
}

/**
 * Calendar months from the current month through the month holding
 * `maxSelectableDate`, inclusive. The stacked calendar has no next/previous
 * buttons, so it renders all of them to keep every selectable date reachable.
 */
export const CALENDAR_MONTHS_COUNT =
    (maxSelectableDate.year - sarsaLocalToday.year) * 12 +
    (maxSelectableDate.month - sarsaLocalToday.month) +
    1;

/** How many months, starting with the current one, the flexible tab offers. */
export const SELECTABLE_MONTHS_COUNT = 12;

/** First day of the current month, the earliest month the flexible tab offers. */
export function getFirstSelectableMonth(): CalendarDate {
    return today(getLocalTimeZone()).set({ day: 1 });
}

function parseMonth(month: string): CalendarDate | null {
    try {
        return parseDate(month).set({ day: 1 });
    } catch {
        return null;
    }
}

/**
 * Keeps only the months the flexible tab offers, as first-of-month ISO strings.
 * Drops past, too-distant, malformed, and duplicate months, which can arrive
 * from an old URL or saved search and couldn't otherwise be deselected.
 */
export function getSelectableMonths(months: string[] | null | undefined): string[] {
    const firstMonth = getFirstSelectableMonth();
    const lastMonth = firstMonth.add({ months: SELECTABLE_MONTHS_COUNT - 1 });
    const selectable = new Set<string>();

    for (const month of months ?? []) {
        const date = parseMonth(month);
        if (date && date.compare(firstMonth) >= 0 && date.compare(lastMonth) <= 0) {
            selectable.add(date.toString());
        }
    }

    return [...selectable];
}

export function getHasSetDates(state: DatePickerState) {
    const hasSpecificDates = !!state.startDate && !!state.endDate;
    const hasFlexibleDates =
        !!state.availMonths && state.availMonths.length > 0 && !!state.availNights;
    const currentTabHasDates =
        (state.activeTab === 'dates' && hasSpecificDates) ||
        (state.activeTab === 'flexible' && hasFlexibleDates);

    return {
        hasSpecificDates,
        hasFlexibleDates,
        hasDates: hasSpecificDates || hasFlexibleDates,
        currentTabHasDates,
    };
}

/**
 * Maps committed filters to the picker's draft state.
 * When flexible dates aren't allowed, the flexible-only fields are dropped so
 * the picker never shows or commits a selection the user can't see or edit.
 */
export function externalToInternalState(
    filters: FlexDateFilters,
    isFlexibleAllowed: boolean
): DatePickerState {
    const startDate = filters.startDate || null;
    const endDate = filters.endDate || null;

    if (!isFlexibleAllowed) {
        return {
            activeTab: 'dates',
            startDate,
            endDate,
            availMonths: null,
            availNights: null,
            flexDays: null,
        };
    }

    const selectableMonths = getSelectableMonths(filters.availMonths);
    const availMonths = selectableMonths.length ? selectableMonths : null;
    const availNights = filters.availNights || null;

    return {
        activeTab: availMonths && availNights ? 'flexible' : 'dates',
        startDate,
        endDate,
        availMonths,
        availNights,
        flexDays: filters.flexStartRange ?? filters.flexEndRange ?? null,
    };
}

/**
 * Maps the draft to the filters that closing the picker commits.
 * Only the active tab's selection is committed; the other tab's is dropped.
 */
export function internalToExternalState(state: DatePickerState): FlexDateFilters {
    const { hasSpecificDates, hasFlexibleDates } = getHasSetDates(state);

    if (state.activeTab === 'flexible') {
        // Months and nights only mean something together, so commit both or neither.
        return {
            startDate: null,
            endDate: null,
            flexStartRange: null,
            flexEndRange: null,
            availMonths: hasFlexibleDates ? state.availMonths : null,
            availNights: hasFlexibleDates ? state.availNights : null,
        };
    }

    // A ± day range only means something around a check-in and check-out.
    // "Exact dates" (0) commits as no range, matching the legacy picker.
    const flexRange = hasSpecificDates ? state.flexDays || null : null;

    return {
        startDate: state.startDate || null,
        endDate: state.endDate || null,
        flexStartRange: flexRange,
        flexEndRange: flexRange,
        availMonths: null,
        availNights: null,
    };
}

function dateKey(date: CalendarDate | null | undefined): string | null {
    return date ? date.toString() : null;
}

function monthsKey(months: string[] | null | undefined): string | null {
    return months?.length ? [...months].sort().join(',') : null;
}

/**
 * Whether two sets of filters describe the same search.
 * Missing and null fields match, and month order is ignored.
 */
export function areFiltersEqual(a: FlexDateFilters, b: FlexDateFilters): boolean {
    return (
        dateKey(a.startDate) === dateKey(b.startDate) &&
        dateKey(a.endDate) === dateKey(b.endDate) &&
        (a.flexStartRange ?? null) === (b.flexStartRange ?? null) &&
        (a.flexEndRange ?? null) === (b.flexEndRange ?? null) &&
        monthsKey(a.availMonths) === monthsKey(b.availMonths) &&
        (a.availNights ?? null) === (b.availNights ?? null)
    );
}

/**
 * What committing `draft` over `committed` would do.
 * Removing means dates were committed and the draft commits none.
 */
export function getCloseAction(
    draft: FlexDateFilters,
    committed: FlexDateFilters
): DatePickerCloseAction {
    if (areFiltersEqual(draft, committed)) {
        return 'close';
    }

    return areFiltersEqual(draft, {}) ? 'remove' : 'apply';
}

/**
 * Whether two optional dates are the same day. Compares by ISO string, so a
 * `CalendarDate` subclass matches a plain `CalendarDate`.
 */
export function areDatesEqual(
    a: CalendarDate | null | undefined,
    b: CalendarDate | null | undefined
): boolean {
    return dateKey(a) === dateKey(b);
}

/**
 * What committing a single-date `draft` over `committed` would do.
 */
export function getSingleDateCloseAction(
    draft: CalendarDate | null,
    committed: CalendarDate | null
): DatePickerCloseAction {
    if (areDatesEqual(draft, committed)) {
        return 'close';
    }

    return draft ? 'apply' : 'remove';
}

/**
 * Trigger text for the single-date picker, e.g. "Oct 20", with the year added
 * when it isn't this year.
 */
export function useGetSingleDateTriggerContent(date: CalendarDate | null): string {
    const formatDateTime = useFormatDateTime();

    if (!date) {
        return 'Add Date ...';
    }

    const isThisYear = date.year === today(getLocalTimeZone()).year;

    return formatDateTime(date, {
        month: 'short',
        day: 'numeric',
        year: isThisYear ? undefined : 'numeric',
    });
}

export function useGetTriggerContent(
    state: DatePickerState,
    addDaysSuffix: boolean = false
): React.ReactNode {
    const formatDateRange = useFormatDateRange();
    const formatDateTime = useFormatDateTime();

    const { hasSpecificDates, hasFlexibleDates } = getHasSetDates(state);

    const localToday = today(getLocalTimeZone());
    const inDifferentYears =
        state.startDate?.year !== state.endDate?.year ||
        state.startDate?.year !== localToday.year;

    if (state.activeTab === 'dates' && hasSpecificDates) {
        const dateRangeText = formatDateRange(state.startDate!, state.endDate!, {
            month: 'short',
            day: 'numeric',
            year: inDifferentYears ? 'numeric' : undefined,
        });

        if (!state.flexDays) {
            return dateRangeText;
        }

        const daysSuffix = pluralize(state.flexDays, 'day', 'days');

        return (
            <>
                {dateRangeText}{' '}
                <span data-element="flex-days">
                    <span aria-hidden="true">
                        <PlusMinusIconSmall />
                        {state.flexDays}
                        {addDaysSuffix && ` ${daysSuffix}`}
                    </span>
                    <span className="rec-sr-only">
                        {`plus or minus ${state.flexDays} ${daysSuffix}`}
                    </span>
                </span>
            </>
        );
    }

    if (state.activeTab === 'flexible' && hasFlexibleDates) {
        const rawMonths = state.availMonths ?? [];

        const months = rawMonths.map((month) => {
            const date = parseDate(month);
            return formatDateTime(date, { month: 'short' });
        });

        return `${state.availNights} ${state.availNights === 1 ? 'Night' : 'Nights'} in ${months.join(', ')}`;
    }

    return 'Add Dates ...';
}

export function PlusMinusIconSmall() {
    return (
        <svg
            width="9"
            height="9"
            viewBox="0 0 9 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="plus-minus-icon"
            aria-hidden="true"
        >
            <path d="M7 8H2V7H7V8ZM5 3H7V4H5V6H4V4H2V3H4V1H5V3Z" fill="currentColor" />
        </svg>
    );
}
